import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { TableSortArgs } from '../../components/TableV2';
import { PAGE_PROPS } from '../constants/common.constants';
import { LAB_DIAGNOSTIC_INITIAL_STATE } from '../constants/labDiagnosticSlice.constants';

export const getAllLabOrdersMaster = createAsyncThunk(
    'labDiagnostic/get',
    async (data: TableSortArgs) => {
        const response = await axios.post('/LabOrder/Search', data);
        return response.data;
    },
);

export const getAllLabOrdersMasterList = createAsyncThunk(
    'labDiagnostic/getList',
    async () => {
        const response = await axios.get('/LabOrder/GetAll');
        return response.data;
    },
);

export const saveLabMaster = createAsyncThunk(
    'labDiagnostic/saveMaster',
    async (
        data: {
            id?: number;
            labServiceArea: string;
            labServiceSubArea: string;
            fees?: number | null;
            createdBy?: string;
        },
        { rejectWithValue },
    ) => {
        try {
            // NOTE: App axios error interceptor returns the error object instead of
            // Promise.reject, so HTTP 4xx can resolve here as an AxiosError.
            const response: {
                data?: {
                    responseCode?: string;
                    responseMessage?: string;
                    result?: unknown;
                };
                response?: {
                    data?: {
                        responseCode?: string;
                        responseMessage?: string;
                        result?: unknown;
                    };
                };
            } = await axios.post('/LabOrder/Master/Save', {
                id: data.id ?? 0,
                labServiceArea: data.labServiceArea,
                labServiceSubArea: data.labServiceSubArea,
                fees: data.fees,
                createdBy: data.createdBy,
            });
            const body = response?.data ?? response?.response?.data;
            if (!body || String(body.responseCode) !== '200') {
                const msg = body?.responseMessage || '';
                return rejectWithValue(
                    body
                        ? {
                              ...body,
                              responseMessage: /already exists/i.test(
                                  String(msg),
                              )
                                  ? 'This lab service already exists in the global catalog. Please select it from the dropdown.'
                                  : msg || 'Failed to save lab service',
                          }
                        : {
                              responseCode: '400',
                              responseMessage: 'Failed to save lab service',
                          },
                );
            }
            return body;
        } catch (err: unknown) {
            const axiosErr = err as {
                response?: {
                    data?: { responseCode?: string; responseMessage?: string };
                };
            };
            return rejectWithValue(
                axiosErr?.response?.data || {
                    responseCode: '400',
                    responseMessage: 'Failed to save lab service',
                },
            );
        }
    },
);

export const deactivateLabMaster = createAsyncThunk(
    'labDiagnostic/deactivateMaster',
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                `/LabOrder/Master/Deactivate/${id}`,
            );
            return response.data;
        } catch (err: unknown) {
            const axiosErr = err as {
                response?: { data?: { responseCode?: string; responseMessage?: string } };
            };
            return rejectWithValue(
                axiosErr?.response?.data || {
                    responseCode: '400',
                    responseMessage: 'Failed to deactivate lab service',
                },
            );
        }
    },
);

export const reactivateLabMaster = createAsyncThunk(
    'labDiagnostic/reactivateMaster',
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                `/LabOrder/Master/Reactivate/${id}`,
            );
            return response.data;
        } catch (err: unknown) {
            const axiosErr = err as {
                response?: { data?: { responseCode?: string; responseMessage?: string } };
            };
            return rejectWithValue(
                axiosErr?.response?.data || {
                    responseCode: '400',
                    responseMessage: 'Failed to reactivate lab service',
                },
            );
        }
    },
);

export const labDiagnostic = createSlice({
    name: 'labDiagnostic',
    initialState: LAB_DIAGNOSTIC_INITIAL_STATE,
    reducers: {
        onLabDiagnosticPageChange: (state, action) => {
            state.pageProps = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAllLabOrdersMaster.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(getAllLabOrdersMaster.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                state.data = action.payload?.result || PAGE_PROPS;
                state.error = null;
            })
            .addCase(getAllLabOrdersMaster.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(getAllLabOrdersMasterList.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(getAllLabOrdersMasterList.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                state.labOrderMaterList = action.payload?.result || [];
                state.error = null;
            })
            .addCase(getAllLabOrdersMasterList.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            });
    },
});

export const { onLabDiagnosticPageChange } = labDiagnostic.actions;

export default labDiagnostic.reducer;
